import { Injectable, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { AngularFirestore, DocumentChangeAction } from '@angular/fire/compat/firestore';
import { Timestamp } from '@angular/fire/firestore';
import { Collection } from '../_types/collection.type';
import { JsAssetMetadata } from '../_interfaces/others.interface';
import { JsEmail } from '../_interfaces/email.interface';
import { JsInvitee, JsUser } from '../_interfaces/user.interface';
import { convertTimeStampsFb_Js } from '../_utils/utils';

type CollectionType = Collection;

// Base interface for all entities with required fields
interface BaseEntity {
  id: string;
  cloudUpdatedAt?: any;
  deletedAt?: any;
}

/**
 * Centralized Firestore Store Service
 *
 * Single source of truth for all Firestore collections.
 * Provides real-time updates using efficient cursor-based listeners.
 *
 * Features:
 * - Automatic real-time updates (no manual refresh needed)
 * - Efficient incremental fetching using cloudUpdatedAt cursor
 * - Angular Signals for reactive state management
 * - Computed signals for filtered views
 *
 * Usage:
 * - Inject this service in components
 * - Access collections via readonly signals: store.emails(), store.users()
 * - Use computed signals for filtered data: store.activeEmails()
 */
@Injectable({
  providedIn: 'root'
})
export class FirestoreStoreService {
  private afs = inject(AngularFirestore);

  // ═══════════════════════════════════════════════════════════════
  // STATE: Single source of truth (Angular Signals)
  // ═══════════════════════════════════════════════════════════════

  // Private writable signals
  private _users = signal<JsUser[]>([]);
  private _emails = signal<JsEmail[]>([]);
  private _invitees = signal<JsInvitee[]>([]);
  private _assets = signal<JsAssetMetadata[]>([]);

  // Public readonly signals (consumers can't modify directly)
  readonly users = this._users.asReadonly();
  readonly emails = this._emails.asReadonly();
  readonly invitees = this._invitees.asReadonly();
  readonly assets = this._assets.asReadonly();

  // Loading states
  readonly usersLoading = signal(true);
  readonly emailsLoading = signal(true);
  readonly inviteesLoading = signal(true);
  readonly assetsLoading = signal(true);

  // ═══════════════════════════════════════════════════════════════
  // COMPUTED: Filtered views (automatically reactive!)
  // ═══════════════════════════════════════════════════════════════

  /**
   * Helper to check if an email template is valid (has all required fields)
   * Validates that the template matches the current data structure requirements
   */
  private isValidEmail(e: JsEmail): boolean {
    // Must not be deleted
    if (e.deletedAt) return false;

    // Must have essential metadata fields
    if (!e.templateName || typeof e.templateName !== 'string') return false;
    if (!e.description || typeof e.description !== 'string') return false;

    // Must have version number (required for versioning)
    if (typeof e.version !== 'number' || e.version < 1) return false;

    // Content hash can be empty string for new templates (will be computed on first save)
    if (typeof e.contentHash !== 'string') return false;

    // Must have content objects (htmlPart and subjectPart)
    if (!e.htmlPart || typeof e.htmlPart !== 'object') return false;
    if (!e.subjectPart || typeof e.subjectPart !== 'object') return false;

    // Must have templateVariables array (even if empty)
    if (!Array.isArray(e.templateVariables)) return false;

    // Must have defaultLangCode
    if (!e.defaultLangCode || typeof e.defaultLangCode !== 'string') return false;

    // Must have at least one language key in both htmlPart and subjectPart
    // (values can be empty strings for new templates - user will fill them in)
    const hasLanguageKeys = Object.keys(e.htmlPart).length > 0 &&
                            Object.keys(e.subjectPart).length > 0;
    if (!hasLanguageKeys) return false;

    // Ensure the defaultLangCode key exists in both htmlPart and subjectPart
    // (values can be empty strings - we just check the key exists)
    if (!(e.defaultLangCode in e.htmlPart) || !(e.defaultLangCode in e.subjectPart)) return false;

    return true;
  }

  /**
   * Active (non-deleted) emails, sorted by updatedAt desc
   * Filters out invalid/incomplete templates that are missing essential fields
   */
  readonly activeEmails = computed(() => {
    const emails = this._emails().filter(e => this.isValidEmail(e));

    return emails.sort((a, b) => {
      const aTime = a.updatedAt?.getTime() || 0;
      const bTime = b.updatedAt?.getTime() || 0;
      return bTime - aTime;
    });
  });

  /**
   * Active (non-deleted) users
   */
  readonly activeUsers = computed(() =>
    this._users().filter(u => !u.deletedAt)
  );

  /**
   * Pending invitees (status = 'pending')
   */
  readonly pendingInvitees = computed(() =>
    this._invitees().filter(i => i.status === 'pending')
  );

  /**
   * Accepted invitees (status = 'accepted')
   */
  readonly acceptedInvitees = computed(() =>
    this._invitees().filter(i => i.status === 'accepted')
  );

  /**
   * Active (non-deleted) assets, sorted by updatedAt desc
   */
  readonly activeAssets = computed(() => {
    const assets = this._assets().filter(a => !a.deletedAt);
    return assets.sort((a, b) => {
      const aTime = a.updatedAt?.getTime() || 0;
      const bTime = b.updatedAt?.getTime() || 0;
      return bTime - aTime;
    });
  });

  // ═══════════════════════════════════════════════════════════════
  // CURSOR TRACKING: Last sync timestamp per collection
  // ═══════════════════════════════════════════════════════════════

  private lastSync: Record<CollectionType, Timestamp | null> = {
    users: null,
    emails: null,
    invitees: null,
    assets: null
  };

  private listeners: Record<CollectionType, (() => void) | null> = {
    users: null,
    emails: null,
    invitees: null,
    assets: null
  };

  private initialized = false;

  // ═══════════════════════════════════════════════════════════════
  // INITIALIZATION: Called once on app start (after authentication)
  // ═══════════════════════════════════════════════════════════════

  /**
   * Initialize all collections with initial data and set up real-time listeners
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      console.warn('[FirestoreStore] Already initialized, skipping...');
      return;
    }

    console.log('[FirestoreStore] Initializing all collections...');

    await Promise.all([
      this.initCollection<JsUser>('users', this._users as any, this.usersLoading),
      this.initCollection<JsEmail>('emails', this._emails as any, this.emailsLoading),
      this.initCollection<JsInvitee>('invitees', this._invitees as any, this.inviteesLoading),
      this.initCollection<JsAssetMetadata>('assets', this._assets as any, this.assetsLoading),
    ]);

    this.initialized = true;
    console.log('[FirestoreStore] Initialization complete');
  }

  /**
   * Initialize a single collection:
   * 1. Fetch all items initially (one-time)
   * 2. Set cursor to max cloudUpdatedAt
   * 3. Set up real-time listener for future updates
   */
  private async initCollection<T extends BaseEntity>(
    collection: CollectionType,
    signalRef: WritableSignal<T[]>,
    loadingRef: WritableSignal<boolean>
  ): Promise<void> {
    try {
      console.log(`[FirestoreStore] Loading ${collection}...`);

      // Step 1: Fetch ALL items initially (one-time snapshot)
      const snapshot = await this.afs
        .collection<any>(collection)
        .get()
        .toPromise();

      const items = (snapshot?.docs || []).map(doc => ({
        ...convertTimeStampsFb_Js(doc.data() as any),
        id: doc.id
      })) as unknown as T[];

      console.log(`[FirestoreStore] Loaded ${items.length} ${collection}`);

      signalRef.set(items);
      loadingRef.set(false);

      // Step 2: Track the max cloudUpdatedAt as cursor
      this.updateCursor(collection, items);

      // Step 3: Set up real-time listener for FUTURE updates only
      this.setupRealtimeListener(collection, signalRef);

      console.log(`[FirestoreStore] Real-time listener active for ${collection}`);
    } catch (error) {
      console.error(`[FirestoreStore] Error initializing ${collection}:`, error);
      loadingRef.set(false);
      throw error;
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // REAL-TIME LISTENER: Only fetches items updated after cursor
  // ═══════════════════════════════════════════════════════════════

  /**
   * Set up Firestore onSnapshot listener that only receives updates
   * for documents with cloudUpdatedAt > cursor timestamp
   */
  private setupRealtimeListener<T extends { id: string; cloudUpdatedAt?: Date | null }>(
    collection: CollectionType,
    signalRef: WritableSignal<T[]>
  ): void {
    // Unsubscribe from previous listener if exists
    this.listeners[collection]?.();

    // Use cursor (or epoch if no cursor set)
    const cursor = this.lastSync[collection] || Timestamp.fromDate(new Date(0));

    console.log(`[FirestoreStore] Setting up listener for ${collection} with cursor:`, cursor.toDate());

    // Set up Firestore snapshot listener with cursor filter
    this.listeners[collection] = this.afs.firestore
      .collection(collection)
      .where('cloudUpdatedAt', '>', cursor)
      .onSnapshot((snapshot) => {
        if (snapshot.empty) {
          return;
        }

        console.log(`[FirestoreStore] Received ${snapshot.docChanges().length} changes for ${collection}`);

        const changes = snapshot.docChanges();
        const currentItems = signalRef();
        let updatedItems = [...currentItems];

        for (const change of changes) {
          const item = {
            ...convertTimeStampsFb_Js(change.doc.data() as any),
            id: change.doc.id
          } as unknown as T;

          const existingIndex = updatedItems.findIndex(i => i.id === item.id);

          if (change.type === 'added') {
            if (existingIndex >= 0) {
              // Update existing item (in case it was already loaded)
              updatedItems[existingIndex] = item;
              console.log(`[FirestoreStore] Updated existing ${collection}:`, item.id);
            } else {
              // Add new item
              updatedItems.push(item);
              console.log(`[FirestoreStore] Added new ${collection}:`, item.id);
            }
          } else if (change.type === 'modified') {
            if (existingIndex >= 0) {
              updatedItems[existingIndex] = item;
              console.log(`[FirestoreStore] Modified ${collection}:`, item.id);
            } else {
              // Item was modified before we started listening, add it
              updatedItems.push(item);
              console.log(`[FirestoreStore] Added modified ${collection}:`, item.id);
            }
          } else if (change.type === 'removed') {
            // Remove item (rare - we use soft delete)
            updatedItems = updatedItems.filter(i => i.id !== item.id);
            console.log(`[FirestoreStore] Removed ${collection}:`, item.id);
          }
        }

        // Update signal (triggers all computed signals automatically!)
        signalRef.set(updatedItems);

        // Update cursor for next listener setup
        const changedItems = changes.map(c => convertTimeStampsFb_Js(c.doc.data() as any));
        this.updateCursor(collection, changedItems);
      }, (error) => {
        console.error(`[FirestoreStore] Listener error for ${collection}:`, error);
      });
  }

  /**
   * Update the cursor timestamp to the maximum cloudUpdatedAt from items
   */
  private updateCursor(collection: CollectionType, items: any[]): void {
    if (items.length === 0) return;

    const maxTimestamp = items.reduce((max, item) => {
      const ts = item.cloudUpdatedAt;
      if (!ts) return max;

      // Convert Date to Firestore Timestamp
      const itemTs = ts instanceof Date ? Timestamp.fromDate(ts) : ts;

      return !max || itemTs.toMillis() > max.toMillis() ? itemTs : max;
    }, this.lastSync[collection]);

    if (maxTimestamp) {
      this.lastSync[collection] = maxTimestamp;
      console.log(`[FirestoreStore] Updated cursor for ${collection}:`, maxTimestamp.toDate());
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // HELPER METHODS: Get item by ID (returns reactive Signal)
  // ═══════════════════════════════════════════════════════════════

  /**
   * Get a single email by ID (reactive signal)
   * Only returns valid emails with all required fields
   */
  getEmailById(id: string): Signal<JsEmail | undefined> {
    return computed(() => {
      const email = this._emails().find(e => e.id === id);
      // Only return if email exists and is valid
      return email && this.isValidEmail(email) ? email : undefined;
    });
  }

  /**
   * Get a single user by ID (reactive signal)
   */
  getUserById(id: string): Signal<JsUser | undefined> {
    return computed(() => this._users().find(u => u.id === id));
  }

  /**
   * Get a single invitee by ID (reactive signal)
   */
  getInviteeById(id: string): Signal<JsInvitee | undefined> {
    return computed(() => this._invitees().find(i => i.id === id));
  }

  /**
   * Get a single invitee by email (reactive signal)
   */
  getInviteeByEmail(email: string): Signal<JsInvitee | undefined> {
    return computed(() => this._invitees().find(i => i.email === email));
  }

  /**
   * Get a single asset by ID (reactive signal)
   */
  getAssetById(id: string): Signal<JsAssetMetadata | undefined> {
    return computed(() => this._assets().find(a => a.id === id));
  }

  // ═══════════════════════════════════════════════════════════════
  // OPTIMISTIC UPDATES: Add items directly to signals
  // ═══════════════════════════════════════════════════════════════

  /**
   * Optimistically add an email to the store signal
   * Used when creating new emails to avoid waiting for Firestore real-time update
   */
  addEmailOptimistic(email: JsEmail): void {
    const currentEmails = this._emails();
    const existingIndex = currentEmails.findIndex(e => e.id === email.id);

    if (existingIndex >= 0) {
      // Update existing
      const updated = [...currentEmails];
      updated[existingIndex] = email;
      this._emails.set(updated);
    } else {
      // Add new
      this._emails.set([...currentEmails, email]);
    }
    console.log(`[FirestoreStore] Optimistically added email: ${email.id}`);
  }

  /**
   * Optimistically update an email in the store signal
   */
  updateEmailOptimistic(email: JsEmail): void {
    const currentEmails = this._emails();
    const existingIndex = currentEmails.findIndex(e => e.id === email.id);

    if (existingIndex >= 0) {
      const updated = [...currentEmails];
      updated[existingIndex] = email;
      this._emails.set(updated);
      console.log(`[FirestoreStore] Optimistically updated email: ${email.id}`);
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // UTILITY: User maps (for backwards compatibility)
  // ═══════════════════════════════════════════════════════════════

  /**
   * Get UID to user map (computed from users signal)
   */
  readonly uidUserMap = computed(() => {
    const map: Record<string, JsUser> = {};
    this._users().forEach(user => {
      if (user.uid) {
        map[user.uid] = user;
      }
    });
    return map;
  });

  /**
   * Get UID to name map (computed from users signal)
   */
  readonly uidToName = computed(() => {
    const map: Record<string, string> = {};
    this._users().forEach(user => {
      if (user.uid) {
        map[user.uid] = user.name;
      }
    });
    return map;
  });

  // ═══════════════════════════════════════════════════════════════
  // CLEANUP: Call on logout/destroy
  // ═══════════════════════════════════════════════════════════════

  /**
   * Cleanup listeners and reset state (call on logout)
   */
  destroy(): void {
    console.log('[FirestoreStore] Destroying listeners and clearing state...');

    // Unsubscribe from all listeners
    Object.values(this.listeners).forEach(unsub => unsub?.());

    // Reset all signals
    this._users.set([]);
    this._emails.set([]);
    this._invitees.set([]);
    this._assets.set([]);

    // Reset loading states
    this.usersLoading.set(true);
    this.emailsLoading.set(true);
    this.inviteesLoading.set(true);
    this.assetsLoading.set(true);

    // Reset cursors
    this.lastSync = {
      users: null,
      emails: null,
      invitees: null,
      assets: null
    };

    // Reset listeners
    this.listeners = {
      users: null,
      emails: null,
      invitees: null,
      assets: null
    };

    this.initialized = false;

    console.log('[FirestoreStore] Cleanup complete');
  }
}
